/*
  This function will be used for individual field validation.
  Parameter validationList - accept array of objects where fieldType and fieldValue are required
  and customMessage is optional. if customMessage is not provided then the
  default message will be used.
*/
export const validateAField = (validationList, options = {}) => {
  const validatedTest = [];
  validationList.forEach((validation) => {
    const validatedField = handleValidation(
      validation.fieldType,
      validation.fieldValue,
      validation.customMessage ? validation.customMessage : null,
      options,
    );

    if (validatedField.isError) {
      validatedTest.push(validatedField);
    }
  });

  if (validatedTest.length > 0) {
    return validatedTest[0];
  }

  return { errorMessage: '', isError: false };
};

/*
  In this function, each mandatory field validation rule will be defined. This allows to
  remove duplicated validation rules in different components where the field is imported.
*/
export const handleValidation = (validationType, validationField, customErrorMessage, options = {}) => {
  const defaultMessage = { errorMessage: '', isError: false };
  switch (validationType) {
    case 'blankField':
      if (validationField === '') {
        return {
          errorMessage: customErrorMessage || validationDefaultMessage(validationType),
          isError: true,
        };
      }
      return defaultMessage;

    case 'minimumOption':
      const optionsWithoutBlanks = validationField.filter((option) => option);

      if (!optionsWithoutBlanks.length) {
        return {
          errorMessage: customErrorMessage || validationDefaultMessage(validationType),
          isError: !optionsWithoutBlanks.length,
        };
      }

      return defaultMessage;

    case 'maximumCharacters':
      if (validationField.length > options.maxChars) {
        return {
          errorMessage:
            customErrorMessage ||
            validationDefaultMessage(validationType, {
              maxChars: options.maxChars,
              currentLength: validationField.length,
            }),
          isError: true,
        };
      }

      return defaultMessage;

    default:
      return defaultMessage;
  }
};

/*
  This function is used to define the default validation messages. 
  This allows to manage all the messages in one place and no need to define in individual fields.
*/
export const validationDefaultMessage = (validationType, options = {}) => {
  switch (validationType) {
    case 'blankField':
      return 'This is a required field.';

    case 'minimumOption':
      return 'Please provide an option.';

    case 'maximumCharacters':
      return `Maximum ${options.maxChars} characters (currently ${options.currentLength}).`;

    default:
      return '';
  }
};

/*
  This function can be used to validate the whole Form template. It accepts two parameters.
  formTemplate - All the values of a form template.
  returnFieldIds - If this is set to true, validateFormTemplate() will return the invalid fieldset IDs. 
  returnFieldIds - If this is set to false, validateFormTemplate() will return true if form is valid and false if form is invalid.
*/
export const validateFormTemplate = (formTemplate, returnFieldIds) => {
  let isFormTemplateValid = true;
  const validateMainFields = validateFormTemplateMainFields(formTemplate);
  const validateAllFieldSets = validateTemplateFieldSets(formTemplate);

  if (validateMainFields.isError || validateAllFieldSets.isError) {
    isFormTemplateValid = false;
  }

  return returnFieldIds && validateAllFieldSets.invalidFieldPositions.length > 0
    ? validateAllFieldSets.invalidFieldPositions
    : isFormTemplateValid;
};

/*
  This function will handle all the form template main fields except each fieldset's fields.
  The main form template fields can be defined as an array item.
  This function is used when the whole form is validated thus this is currently called in validateFormTemplate().
*/
export const validateFormTemplateMainFields = (formTemplate) => {
  const templateRequiredFields = ['title'];
  let isError = false;

  for (const requiredField of templateRequiredFields) {
    if (autoValidateField(requiredField, formTemplate).isError) {
      isError = true;
    }
  }

  return { isError };
};

/*
  This function will handle all the fieldset fields.
  This function is used when the whole form is validated thus this is currently called in validateFormTemplate().
*/
export const validateTemplateFieldSets = (formTemplate) => {
  const fieldsetRequiredFields = ['title', 'options'];
  const invalidFieldPositions = [];
  let isError = false;

  for (const templateField of formTemplate.fields) {
    for (const requiredField of fieldsetRequiredFields) {
      const fieldValidation = autoValidateField(requiredField, templateField);
      if (fieldValidation.isError) {
        isError = true;

        if (
          fieldValidation.invalidFieldId !== null &&
          !invalidFieldPositions.includes(fieldValidation.invalidFieldId)
        ) {
          invalidFieldPositions.push(fieldValidation.invalidFieldId);
        }
      }
    }
  }

  return {
    isError,
    invalidFieldPositions,
  };
};

/*
  This function is used when the whole form template is validated. 
  This function will set the correct values for mandatory fields and execute validateAField()
  to return the validation status.
*/
export const autoValidateField = (fieldName, field) => {
  switch (fieldName) {
    case 'options':
      const optionFields = ['checkbox', 'radio', 'dropdown'];

      if (optionFields.includes(field.field_type)) {
        return {
          ...validateAField([{ fieldType: 'minimumOption', fieldValue: field.options }]),
          invalidFieldId: field.id,
        };
      }

    case 'title':
      return {
        ...validateAField([{ fieldType: 'blankField', fieldValue: field.title }]),
        invalidFieldId: field.id,
      };

    default:
      return true;
  }
};
